/**
 * Development logging utilities
 * These logs only run in development/staging environments, not production
 */

/**
 * Log a warning message in development only
 * @param message - Warning message or data to log
 */
export function warn(message: unknown, ...args: unknown[]): void {
  if (process.env.NODE_ENV !== "production") {
    console.warn(message, ...args)
  }
}

/**
 * Log an info message in development only
 * @param message - Info message or data to log
 */
export function info(message: unknown, ...args: unknown[]): void {
  if (process.env.NODE_ENV !== "production") {
    console.info(message, ...args)
  }
}

/**
 * Safely extract error message from any error value
 *
 * In JavaScript/TypeScript, caught errors can be any type (Error, string, number, null, etc.).
 * This function safely extracts a meaningful string message from any error value.
 *
 * @param error - Any value caught in catch block
 * @returns Safe error message string
 *
 * @example
 * ```ts
 * try {
 *   throw new Error("Something went wrong")
 * } catch (error) {
 *   const message = getErrorMessage(error) // "Something went wrong"
 *   log(message)
 * }
 * ```
 */
export function getErrorMessage(error: unknown): string {
  if (error == null) {
    return "Unknown error"
  }
  if (typeof error === "string") {
    return error
  }
  if (error instanceof Error) {
    return error.message
  }
  if (typeof error === "object" && "message" in error && typeof error.message === "string") {
    return error.message
  }
  return String(error)
}
